import { ContextSource, Signal, ReadinessBreakdown } from '../types/context.types';

// Mirrors revealer-backend/src/services/contextService.ts#calculateReadiness.
// Used for optimistic client-side updates when signals/sources mutate, so the
// Context Readiness meter moves in the same frame as the user's action rather
// than waiting for the server round-trip. Keep in sync with the backend.

const RELEVANT_CATEGORIES = ['goal', 'audience', 'tone', 'constraint'] as const;

const pointsForCategoryCount = (n: number): number => {
  if (n <= 0) return 0;
  if (n === 1) return 6;
  if (n === 2) return 9;
  return 10;
};

// Signal/source mutations don't affect the interactions pillar, so callers
// doing an optimistic update can pass the current { score, conversationCount }
// from the last server response to preserve it untouched. Callers that have
// raw message counts (e.g., post-chat) can pass { totalMessages } instead.
export type InteractionsInput =
  | { conversationCount: number; totalMessages: number }
  | { conversationCount: number; score: number };

export function computeReadiness(
  sources: ContextSource[],
  signals: Signal[],
  interactions: InteractionsInput
): { score: number; breakdown: ReadinessBreakdown } {
  // --- Data Sources pillar (max 40) ---
  let dataSourcesScore = 0;
  const sourceCount = sources.length;
  if (sourceCount >= 1) dataSourcesScore += 15;
  if (sourceCount >= 2) dataSourcesScore += 10;
  const additionalSources = Math.min(sourceCount - 2, 3);
  if (additionalSources > 0) dataSourcesScore += additionalSources * 5;
  const allReady = sources.length > 0 && sources.every((s) => s.status === 'ready');
  if (allReady && sources.length > 0) dataSourcesScore += 10;
  dataSourcesScore = Math.min(dataSourcesScore, 40);

  // --- Manual Tags pillar (max 40) ---
  const activeSignals = signals.filter((s) => s.isActive);
  const categoryCounts = new Map<string, number>();
  for (const signal of activeSignals) {
    if ((RELEVANT_CATEGORIES as readonly string[]).includes(signal.category)) {
      categoryCounts.set(signal.category, (categoryCounts.get(signal.category) || 0) + 1);
    }
  }
  let manualTagsScore = 0;
  for (const category of RELEVANT_CATEGORIES) {
    manualTagsScore += pointsForCategoryCount(categoryCounts.get(category) || 0);
  }
  manualTagsScore = Math.min(manualTagsScore, 40);
  const manualTagCount = Array.from(categoryCounts.values()).reduce((a, b) => a + b, 0);

  // --- Interactions pillar (max 20) ---
  const interactionsScore =
    'score' in interactions
      ? Math.min(20, Math.max(0, interactions.score))
      : Math.min(
          20,
          interactions.conversationCount * 4 + Math.floor(interactions.totalMessages / 2)
        );

  const chatExtractedSignalCount = signals.filter((s) => s.sourceType === 'chat').length;

  const totalScore = Math.min(dataSourcesScore + manualTagsScore + interactionsScore, 100);

  const breakdown: ReadinessBreakdown = {
    dataSourcesScore,
    sourceCount,
    hasMultipleSources: sourceCount >= 2,
    hasProcessedSources: allReady,
    manualTagsScore,
    manualTagCount,
    hasGoalTag: (categoryCounts.get('goal') || 0) > 0,
    hasAudienceTag: (categoryCounts.get('audience') || 0) > 0,
    hasToneTag: (categoryCounts.get('tone') || 0) > 0,
    hasConstraintTag: (categoryCounts.get('constraint') || 0) > 0,
    interactionsScore,
    conversationCount: interactions.conversationCount,
    chatExtractedSignalCount,
  };

  return { score: totalScore, breakdown };
}
